import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/common/PageHeader';
import SearchButton from '../components/common/SearchButton';
import { useLeagues } from '../contexts/DataContext';
import { resolveLeagueIcon } from '../utils/leagueIcon';

const LeagueListPage: React.FC = () => {
  const { leagues, fetch: fetchLeagues } = useLeagues();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    loadLeagueData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadLeagueData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLeagueData = async () => {
    try {
      setLoading(true);
      await fetchLeagues();
    } catch (err) {
      setError('Failed to fetch leagues');
      console.error('Error fetching leagues:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter leagues based on search query (excluding game-specific leagues)
  const filteredLeagues = useMemo(() => {
    let result = leagues.filter(league => !league.isGameSpecificLeague);
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(league => {
        const nameMatch = league.name.toLowerCase().includes(query);
        const descMatch = league.description?.toLowerCase().includes(query);
        return nameMatch || descMatch;
      });
    }
    // Sort by number of plays (descending)
    result.sort((a, b) => (b.stats?.totalGamesPlayed || 0) - (a.stats?.totalGamesPlayed || 0));
    return result;
  }, [leagues, searchQuery]);

  if (loading) {
    return (
      <div>
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-body">Loading leagues...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <PageHeader className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-fluid-h1 font-bold text-heading">Leagues</h1>
          <p className="mt-2 text-body">Manage player leagues and view rankings</p>
        </div>
        <div className="flex items-center gap-2">
          <SearchButton
            isExpanded={searchExpanded}
            onToggle={() => setSearchExpanded(!searchExpanded)}
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search leagues..."
          />
          <button
            data-track="league_new"
            onClick={() => navigate('/leagues/new')}
            data-testid="create-league-button"
            className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-700 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            aria-label="Create league"
          >
            <PlusIcon className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Create League</span>
          </button>
        </div>
      </PageHeader>

      {/* Subtitle */}
      {leagues.length > 0 && searchQuery && (
        <div className="mb-6">
          <p className="text-body text-sm">
            Showing {filteredLeagues.length} of {leagues.length} leagues
          </p>
        </div>
      )}

      {/* Leagues Grid */}
      {leagues.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-disabled mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-heading mb-2">No leagues found</h3>
          <p className="text-muted mb-4">Create your first league to get started.</p>
          <Link
            data-track="league_new"
            to="/leagues/new"
            className="inline-flex items-center gap-2 rounded-lg border border-transparent bg-primary-700 px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-transform transform hover:scale-105"
          >
            <PlusIcon className="h-5 w-5" aria-hidden="true" />
            <span>Create First League</span>
          </Link>
        </div>
      ) : filteredLeagues.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-disabled mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-heading mb-2">No leagues match your search</h3>
          <p className="text-muted">Try a different search term.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredLeagues.map((league) => {
              const stats = league.stats;

              return (
                <Link
                  data-track="league_card"
                  key={league.id}
                  to={`/leagues/${league.id}`}
                  className={`group block bg-surface rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer transform hover:scale-105 active:scale-[0.98] ${
                    league.isGlobal ? 'border-l-4 border-primary-400 bg-primary-50/30' : 'border border-edge hover:border-primary-300'
                  }`}
                >
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div className="text-xl">
                          {resolveLeagueIcon(league.icon, league.isGlobal ? '🌍' : '🏆')}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-heading">
                            {league.name}
                          </h3>
                        </div>
                      </div>
                      <div className="text-primary-700 opacity-0 group-hover:opacity-100 transition-opacity">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>

                    {stats && (
                      <div className="grid grid-cols-2 gap-2 text-xs text-body">
                        <div className="flex items-center">
                          <svg className="w-3.5 h-3.5 mr-1.5 text-disabled" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                          </svg>
                          <span>{league.playerIds?.length || 0} players</span>
                        </div>
                        <div className="flex items-center">
                          <svg className="w-3.5 h-3.5 mr-1.5 text-disabled" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          <span>{stats.totalGamesPlayed} plays</span>
                        </div>
                        <div className="flex items-center">
                          <svg className="w-3.5 h-3.5 mr-1.5 text-disabled" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2H5a2 2 0 00-2 2v2m14 0h-2" />
                          </svg>
                          <span>{stats.distinctGames} games</span>
                        </div>
                        {stats.lastPlayDate && (
                          <div className="flex items-center">
                            <svg className="w-3.5 h-3.5 mr-1.5 text-disabled" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span>{new Date(stats.lastPlayDate).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Link>
              )
            })}
          </div>
      )}
    </div>
  );
};

export default LeagueListPage;
